import { z } from 'zod';
import { digestSchema, hashPortalToken } from '../hashing/hash.js';
import { personIdSchema } from '../people/person.js';
import { generateAccessCode } from './codes.js';
import { hashAccessCode, toBase64Url } from './kdf.js';
/**
 * Portal tokens: the entire security perimeter for field staff (ADR-004).
 *
 * Nurses, therapists and aides never get an account, ever. A link they were emailed plus a
 * code they were told separately is the whole of it, so every property here is load-bearing.
 *
 * THE RAW TOKEN IS NEVER STORED. Only its digest, under a dedicated hash spec. A dump of
 * this container gives an attacker nothing they can present as a link.
 */
export const tokenPurposeSchema = z.enum([
    /** Opens the person's own onboarding packet. */
    'portal-session',
    /** Scoped to specific step instances — an ad-hoc document request (PLAN §7.3). */
    'doc-request',
]);
export const portalTokenSchema = z.object({
    /**
     * The digest IS the id and the partition key, so a lookup is a point read and a raw token
     * never reaches a query — nothing can accidentally log one in a query string or a
     * diagnostic. Cosmos never sees the secret at all.
     */
    id: digestSchema,
    tokenHash: digestSchema,
    docType: z.literal('portal-token').default('portal-token'),
    personId: personIdSchema,
    purpose: tokenPurposeSchema,
    /**
     * Which step instances this token may see. Empty means the person's whole packet.
     * Enforced on every request, not just at redemption.
     */
    scope: z.object({ instanceIds: z.array(z.string().min(1)).default([]) }),
    accessCodeHash: z.string().min(1),
    issuedAt: z.string().datetime(),
    issuedBy: z.string().min(1),
    expiresAt: z.string().datetime(),
    firstUsedAt: z.string().datetime().nullable().default(null),
    revokedAt: z.string().datetime().nullable().default(null),
    revokedReason: z.string().nullable().default(null),
    failedAttempts: z.number().int().nonnegative().default(0),
    lockedUntil: z.string().datetime().nullable().default(null),
    /**
     * Cosmos row TTL in seconds. Deliberately LONGER than the token's life — see
     * `ttlSecondsFor`. An expired row that has been purged is indistinguishable from a
     * forged one.
     */
    ttl: z.number().int().positive(),
});
/**
 * 32 bytes from the platform CSPRNG.
 *
 * `crypto.getRandomValues` and a local base64url encoder rather than `node:crypto` and
 * `Buffer`, because `apps/web` imports this package and the kernel must build for the
 * browser — the same constraint `hashing/hash.ts` already records.
 *
 * 256 bits is why the token digest needs no key stretching: there is no dictionary to
 * stretch against, and a pre-image search against SHA-256 over a 256-bit CSPRNG value is
 * not a thing an attacker does.
 */
export async function mintToken() {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const token = toBase64Url(bytes);
    const accessCode = generateAccessCode();
    return {
        token,
        accessCode,
        tokenHash: await hashPortalToken(token),
        accessCodeHash: await hashAccessCode(accessCode),
    };
}
/**
 * How long the ROW lives, versus how long the TOKEN works.
 *
 * The row outlives the token by 30 days on purpose. Once Cosmos purges a row, a lookup
 * cannot tell an expired link from one that never existed, and the person is told
 * "invalid link" when the truth is "expired, request another". That wording is precisely
 * the HR support ticket ADR-004's self-service reissue exists to remove.
 */
export const EXPIRY_GRACE_DAYS = 30;
export function ttlSecondsFor(issuedAt, expiresAt) {
    const life = Math.max(0, Math.floor((Date.parse(expiresAt) - Date.parse(issuedAt)) / 1000));
    return life + EXPIRY_GRACE_DAYS * 24 * 60 * 60;
}
/**
 * The lockout ladder. Per TOKEN, never per IP.
 *
 * An agency sits behind one NAT, so per-IP counting would let one candidate's fumbled code
 * lock out everyone else in the building. And it is the right unit anyway: to reach this
 * ladder an attacker must ALREADY hold a valid 256-bit token, so the thing being rate
 * limited is guessing the second factor for one specific person.
 *
 * Eight failures revokes outright rather than locking longer, because at that point the
 * link should be replaced rather than defended.
 */
export const LOCKOUT_LADDER = [
    { atFailure: 5, minutes: 15 },
    { atFailure: 6, minutes: 60 },
    { atFailure: 7, minutes: 60 * 24 },
];
export const REVOKE_AT_FAILURE = 8;
export function nextLockout(failedAttempts, now) {
    if (failedAttempts >= REVOKE_AT_FAILURE)
        return { lockedUntil: null, revoke: true };
    const rung = LOCKOUT_LADDER.find((step) => step.atFailure === failedAttempts);
    if (rung === undefined)
        return { lockedUntil: null, revoke: false };
    return { lockedUntil: new Date(Date.parse(now) + rung.minutes * 60_000).toISOString(), revoke: false };
}
/**
 * A discriminated union, never a boolean.
 *
 * The caller has to handle each refusal separately: `expired` is safe to tell the person
 * (they hold a real token, so it reveals nothing they did not already know), while
 * `not-found` and `revoked` must be indistinguishable from each other. A boolean would
 * collapse that distinction at the one place it matters.
 */
export function verifyToken(input) {
    const { record, now } = input;
    if (record === null)
        return { ok: false, reason: 'not-found' };
    if (record.revokedAt !== null)
        return { ok: false, reason: 'revoked' };
    if (record.lockedUntil !== null && record.lockedUntil > now)
        return { ok: false, reason: 'locked' };
    if (record.expiresAt <= now)
        return { ok: false, reason: 'expired' };
    return {
        ok: true,
        grant: { personId: record.personId, purpose: record.purpose, instanceIds: record.scope.instanceIds },
    };
}
/** An empty scope is the person's whole packet; a non-empty one is exactly those items. */
export function grantCovers(grant, instanceId) {
    return grant.instanceIds.length === 0 || grant.instanceIds.includes(instanceId);
}
/**
 * Where a link that EXISTS stands — one definition, read by both endpoints that answer it.
 *
 * `portalLinkStatusHandler` worked this out inline, and the Portal links list (#303) needs the
 * same verdict for many people at once. Two copies of a two-branch ternary is how the per-person
 * panel and the list come to disagree about one link: the list would call it active while the
 * record calls it expired, and nothing would be wrong anywhere in particular.
 *
 * REVOKED BEATS EXPIRED. A link HR killed must not read as merely lapsed — the two look the same
 * to a candidate and are completely different to whoever revoked it.
 *
 * `<=` rather than `<`, matching `verifyToken`: a token whose `expiresAt` is exactly now no longer
 * opens anything, so a status that still called it active would contradict the door.
 *
 * It deliberately does NOT answer `'none'`. A missing token and a pointer naming one that is gone
 * are facts about the POINTER, not about a record this never sees, and returning a state for a
 * `null` would invite callers to stop distinguishing them (see `portalLinkRowSchema`).
 */
export function portalLinkState(token, now) {
    if (token.revokedAt !== null)
        return 'revoked';
    return token.expiresAt <= now ? 'expired' : 'active';
}
//# sourceMappingURL=tokens.js.map